import React, { forwardRef } from "react";
import { fmtPlain, dmy, totals, amountInWords } from "@/lib/lumpsum";

// Faithful reproduction of the owner's reference lump-sum invoice (SKZ332A.pdf): centred bold letterhead, bill-to on the left,
// "Date / Vehicle No. / Make / Acc Date" block on the right, one description line with the amount, amount in words. No borders, no colour.
const FONT = { fontFamily: "Calibri, Carlito, 'Segoe UI', 'Liberation Sans', Arial, sans-serif" };

const LumpSumDocument = forwardRef(function LumpSumDocument({ inv }, ref) {
  const h = inv.header || {};
  const t = totals(inv.amount, inv.gst_mode, inv.gst_rate);
  const words = inv.amount_words || amountInWords(t.total);
  const addr = (inv.bill_to_address || "").split("\n").filter(Boolean);
  const facts = [["Date", dmy(inv.invoice_date)], ["Vehicle No.", inv.vehicle_no], ["Make", inv.make], ["Acc Date", dmy(inv.accident_date)]];
  if (inv.show_invoice_number !== false && inv.invoice_no) facts.splice(1, 0, ["Invoice No.", inv.invoice_no]);
  if (inv.insurer_claim_no) facts.push(["Claim Ref", inv.insurer_claim_no]);
  const hasGst = inv.gst_mode !== "none" && Number(inv.gst_rate) > 0;
  return (
    <div ref={ref} className="relative bg-white text-black w-[793px] min-h-[1100px] px-[72px] pt-[70px] pb-[60px] text-[14.5px] leading-[1.35]" style={FONT} data-testid="lumpsum-document">
      {inv.status === "draft" && <div className="absolute top-6 right-8 text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 border border-slate-300 px-2 py-0.5 rounded print:hidden" data-testid="lumpsum-draft-mark">Draft</div>}
      {inv.status === "cancelled" && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className="text-[90px] font-black tracking-[0.2em] text-rose-200/70 -rotate-[20deg]">CANCELLED</span></div>}
      <div className="text-center">
        <div className="text-[29px] font-bold leading-tight" data-testid="lumpsum-business-name">{h.business_name}</div>
        <div className="mt-[6px]">{h.address_line}</div>
        <div>{h.contact_line}</div>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-x-6 mt-[46px]">
        <div className="pl-[6px] space-y-[6px]" data-testid="lumpsum-bill-to">
          <div>{inv.bill_to_name}</div>
          {addr.map((l, i) => <div key={i}>{l}</div>)}
        </div>
        <div className="grid grid-cols-[86px_14px_auto] gap-y-[3px] pr-[20px] min-w-[300px]" data-testid="lumpsum-facts">
          {facts.map(([l, v]) => <React.Fragment key={l}><div>{l}</div><div>:</div><div className="whitespace-nowrap">{v}</div></React.Fragment>)}
        </div>
      </div>
      <div className="mt-[90px] grid grid-cols-[1fr_auto] gap-x-8 pl-[108px] pr-[40px]" data-testid="lumpsum-line">
        <div className="whitespace-pre-line">{inv.description}</div>
        <div className="text-right tabular-nums">{hasGst ? fmtPlain(t.subtotal) : fmtPlain(t.total)}</div>
      </div>
      {hasGst && (
        <div className="mt-[14px] grid grid-cols-[1fr_auto] gap-x-8 gap-y-[4px] pl-[108px] pr-[40px] tabular-nums" data-testid="lumpsum-gst">
          <div>GST {Number(inv.gst_rate)}%{inv.gst_mode === "inclusive" ? " (included)" : ""}</div><div className="text-right">{fmtPlain(t.gst_amount)}</div>
          <div className="font-bold">Total</div><div className="text-right font-bold">{fmtPlain(t.total)}</div>
        </div>
      )}
      <div className="mt-[64px] pl-[102px]" data-testid="lumpsum-words">{words}</div>
      {(inv.payment_terms || inv.remarks) && (
        <div className="mt-[70px] pl-[6px] space-y-[8px] text-[13px]" data-testid="lumpsum-footer">
          {inv.payment_terms && <div><span className="font-bold">Payment terms: </span>{inv.payment_terms}</div>}
          {inv.remarks && <div className="whitespace-pre-line"><span className="font-bold">Remarks: </span>{inv.remarks}</div>}
        </div>
      )}
    </div>
  );
});

export default LumpSumDocument;
